import { format, formatDistanceToNow, parseISO, isValid, differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns'
import { tr } from 'date-fns/locale'
import { VALIDATION_RULES, DATE_FORMATS, FILE_SIZE_LIMITS, SUBJECTS } from './constants'
import type { Subject, ProgressData } from '../types'

// Date and Time Utilities
export const formatDate = (date: string | Date, formatStr: string = DATE_FORMATS.SHORT): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    if (!isValid(dateObj)) return 'Geçersiz tarih'
    return format(dateObj, formatStr, { locale: tr })
  } catch {
    return 'Geçersiz tarih'
  }
}

export const formatRelativeTime = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    if (!isValid(dateObj)) return 'Geçersiz tarih'
    return formatDistanceToNow(dateObj, { addSuffix: true, locale: tr })
  } catch {
    return 'Geçersiz tarih'
  }
}

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} saniye`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return remainingSeconds > 0 ? `${minutes}dk ${remainingSeconds}sn` : `${minutes} dakika`
  } else {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return minutes > 0 ? `${hours}sa ${minutes}dk` : `${hours} saat`
  }
}

export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

export const parseTimeString = (timeStr: string): number => {
  const parts = timeStr.split(':').map(Number)
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1] // minutes and seconds
  } else if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2] // hours, minutes, and seconds
  }
  return 0
}

export const getTimeDifference = (startDate: string | Date, endDate: string | Date = new Date()): {
  seconds: number
  minutes: number
  hours: number
  days: number
} => {
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate
  const end = typeof endDate === 'string' ? parseISO(endDate) : endDate

  return {
    seconds: differenceInSeconds(end, start),
    minutes: differenceInMinutes(end, start),
    hours: differenceInHours(end, start),
    days: differenceInDays(end, start)
  }
}

export const isToday = (date: string | Date): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const today = new Date()
  return dateObj.toDateString() === today.toDateString()
}

export const isThisWeek = (date: string | Date): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const today = new Date()
  const weekStart = new Date(today.setDate(today.getDate() - today.getDay()))
  const weekEnd = new Date(today.setDate(today.getDate() - today.getDay() + 6))
  return dateObj >= weekStart && dateObj <= weekEnd
}

export const isThisMonth = (date: string | Date): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const today = new Date()
  return dateObj.getMonth() === today.getMonth() && dateObj.getFullYear() === today.getFullYear()
}

// String Utilities
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const capitalizeWords = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

export const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const truncate = (str: string, length: number, suffix: string = '...'): string => {
  if (str.length <= length) return str
  return str.substring(0, length - suffix.length) + suffix
}

export const removeHtmlTags = (str: string): string => {
  return str.replace(/<[^>]*>/g, '')
}

export const escapeHtml = (str: string): string => {
  const div = document.createElement('div')
  div.textContent = str
  return div.innerHTML
}

export const unescapeHtml = (str: string): string => {
  const div = document.createElement('div')
  div.innerHTML = str
  return div.textContent || div.innerText || ''
}

// Number Utilities
export const formatNumber = (num: number, decimals: number = 0): string => {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num)
}

export const formatCurrency = (amount: number, currency: string = 'TRY'): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency
  }).format(amount)
}

export const formatPercentage = (value: number, total: number, decimals: number = 1): string => {
  if (total === 0) return '0%'
  const percentage = (value / total) * 100
  return `${formatNumber(percentage, decimals)}%`
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max)
}

export const roundToNearest = (value: number, nearest: number): number => {
  return Math.round(value / nearest) * nearest
}

export const randomBetween = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0
  return Math.round((value / total) * 100)
}

export const calculateGrade = (score: number, total: number): string => {
  const percentage = calculatePercentage(score, total)
  
  if (percentage >= 90) return 'A+'
  if (percentage >= 85) return 'A'
  if (percentage >= 80) return 'A-'
  if (percentage >= 75) return 'B+'
  if (percentage >= 70) return 'B'
  if (percentage >= 65) return 'B-'
  if (percentage >= 60) return 'C+'
  if (percentage >= 55) return 'C'
  if (percentage >= 50) return 'C-'
  if (percentage >= 45) return 'D+'
  if (percentage >= 40) return 'D'
  return 'F'
}

// Array Utilities
export const shuffle = <T>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

export const chunk = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)]
}

export const groupBy = <T, K extends keyof any>(array: T[], key: (item: T) => K): Record<K, T[]> => {
  return array.reduce((groups, item) => {
    const group = key(item)
    groups[group] = groups[group] || []
    groups[group].push(item)
    return groups
  }, {} as Record<K, T[]>)
}

export const sortBy = <T>(array: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1
    if (aVal > bVal) return order === 'asc' ? 1 : -1
    return 0
  })
}

export const findByProperty = <T>(array: T[], property: keyof T, value: any): T | undefined => {
  return array.find(item => item[property] === value)
}

export const removeByProperty = <T>(array: T[], property: keyof T, value: any): T[] => {
  return array.filter(item => item[property] !== value)
}

// Object Utilities
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T
  if (typeof obj === 'object') {
    const clonedObj = {} as { [key: string]: any }
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj as T
  }
  return obj
}

export const mergeDeep = (target: any, ...sources: any[]): any => {
  if (!sources.length) return target
  const source = sources.shift()

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} })
        mergeDeep(target[key], source[key])
      } else {
        Object.assign(target, { [key]: source[key] })
      }
    }
  }

  return mergeDeep(target, ...sources)
}

export const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item)
}

export const isEmpty = (value: any): boolean => {
  if (value == null) return true
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

export const pick = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  const result = {} as Pick<T, K>
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

export const omit = <T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const result = { ...obj }
  keys.forEach(key => {
    delete result[key]
  })
  return result
}

// Validation Utilities
export const isValidEmail = (email: string): boolean => {
  return VALIDATION_RULES.EMAIL.test(email)
}

export const isValidPassword = (password: string): boolean => {
  return VALIDATION_RULES.PASSWORD.test(password)
}

export const isValidPhone = (phone: string): boolean => {
  return VALIDATION_RULES.PHONE.test(phone)
}

export const isValidUsername = (username: string): boolean => {
  return VALIDATION_RULES.USERNAME.test(username)
}

export const isValidName = (name: string): boolean => {
  return VALIDATION_RULES.NAME.test(name)
}

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export const validateFileSize = (file: File, maxSize: number = FILE_SIZE_LIMITS.GENERAL): boolean => {
  return file.size <= maxSize
}

export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase()
  return fileExtension ? allowedTypes.includes(fileExtension) : false
}

// Color Utilities
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

export const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

export const getContrastColor = (hexColor: string): string => {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return '#000000'
  
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
  return brightness > 128 ? '#000000' : '#ffffff'
}

export const lightenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  
  const factor = 1 + percent / 100
  const r = Math.min(255, Math.round(rgb.r * factor))
  const g = Math.min(255, Math.round(rgb.g * factor))
  const b = Math.min(255, Math.round(rgb.b * factor))
  
  return rgbToHex(r, g, b)
}

export const darkenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  
  const factor = 1 - percent / 100
  const r = Math.max(0, Math.round(rgb.r * factor))
  const g = Math.max(0, Math.round(rgb.g * factor))
  const b = Math.max(0, Math.round(rgb.b * factor))
  
  return rgbToHex(r, g, b)
}

// Local Storage Utilities
export const setLocalStorage = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error('Error saving to localStorage:', error)
  }
}

export const getLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error('Error reading from localStorage:', error)
    return defaultValue
  }
}

export const removeLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Error removing from localStorage:', error)
  }
}

export const clearLocalStorage = (): void => {
  try {
    localStorage.clear()
  } catch (error) {
    console.error('Error clearing localStorage:', error)
  }
}

// Session Storage Utilities
export const setSessionStorage = (key: string, value: any): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error('Error saving to sessionStorage:', error)
  }
}

export const getSessionStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = sessionStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error('Error reading from sessionStorage:', error)
    return defaultValue
  }
}

export const removeSessionStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    console.error('Error removing from sessionStorage:', error)
  }
}

// URL Utilities
export const getQueryParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search)
  const result: Record<string, string> = {}
  
  for (const [key, value] of params) {
    result[key] = value
  }
  
  return result
}

export const setQueryParam = (key: string, value: string): void => {
  const url = new URL(window.location.href)
  url.searchParams.set(key, value)
  window.history.pushState({}, '', url.toString())
}

export const removeQueryParam = (key: string): void => {
  const url = new URL(window.location.href)
  url.searchParams.delete(key)
  window.history.pushState({}, '', url.toString())
}

export const buildUrl = (baseUrl: string, params: Record<string, any>): string => {
  const url = new URL(baseUrl)
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.set(key, String(value))
    }
  })
  
  return url.toString()
}

// Device Detection
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export const isTablet = (): boolean => {
  const userAgent = navigator.userAgent
  // iPad detection
  if (/iPad/i.test(userAgent)) return true
  
  // Android tablet detection (Android without Mobile keyword)
  if (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) return true
  
  // Additional tablet patterns
  if (/Tablet|PlayBook|Silk/i.test(userAgent)) return true
  
  // Screen size based detection for modern devices
  if (typeof window !== 'undefined' && window.screen) {
    const { width, height } = window.screen
    const minDimension = Math.min(width, height)
    const maxDimension = Math.max(width, height)
    // Tablet range: 768px - 1024px in portrait mode
    if (minDimension >= 768 && maxDimension <= 1366) {
      return true
    }
  }
  
  return false
}

export const isDesktop = (): boolean => {
  return !isMobile() && !isTablet()
}

export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (isMobile()) return 'mobile'
  if (isTablet()) return 'tablet'
  return 'desktop'
}

export const getBrowserInfo = (): { name: string; version: string } => {
  const userAgent = navigator.userAgent
  let browserName = 'Unknown'
  let browserVersion = 'Unknown'

  if (userAgent.indexOf('Chrome') > -1) {
    browserName = 'Chrome'
    browserVersion = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown'
  } else if (userAgent.indexOf('Firefox') > -1) {
    browserName = 'Firefox'
    browserVersion = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown'
  } else if (userAgent.indexOf('Safari') > -1) {
    browserName = 'Safari'
    browserVersion = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown'
  } else if (userAgent.indexOf('Edge') > -1) {
    browserName = 'Edge'
    browserVersion = userAgent.match(/Edge\/(\d+)/)?.[1] || 'Unknown'
  }

  return { name: browserName, version: browserVersion }
}

// Performance Utilities
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void => {
  let timeout: ReturnType<typeof setTimeout> | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void => {
  let inThrottle: boolean
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

export const memoize = <T extends (...args: any[]) => any>(fn: T): T => {
  const cache = new Map()
  
  return ((...args: any[]) => {
    const key = JSON.stringify(args)
    if (cache.has(key)) {
      return cache.get(key)
    }
    
    const result = fn(...args)
    cache.set(key, result)
    return result
  }) as T
}

// Study Utilities
export const calculateStudyStreak = (studySessions: { date: string }[]): number => {
  if (studySessions.length === 0) return 0
  
  const sortedSessions = studySessions
    .map(session => new Date(session.date).toDateString())
    .filter((date, index, array) => array.indexOf(date) === index)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
  
  let streak = 0
  let currentDate = new Date()
  
  for (const sessionDate of sortedSessions) {
    const sessionDateObj = new Date(sessionDate)
    const daysDiff = Math.floor((currentDate.getTime() - sessionDateObj.getTime()) / (1000 * 60 * 60 * 24))
    
    if (daysDiff === streak) {
      streak++
      currentDate = sessionDateObj
    } else if (daysDiff === streak + 1 && streak === 0) {
      // Allow for today not having a session yet
      streak++
      currentDate = sessionDateObj
    } else {
      break
    }
  }
  
  return streak
}

export const calculateExamScore = (correctAnswers: number, totalQuestions: number): {
  score: number
  percentage: number
  grade: string
} => {
  const percentage = calculatePercentage(correctAnswers, totalQuestions)
  const score = Math.round((correctAnswers / totalQuestions) * 100)
  const grade = calculateGrade(correctAnswers, totalQuestions)
  
  return { score, percentage, grade }
}

export const getSubjectById = (subjectId: string): Subject | undefined => {
  return SUBJECTS.find(subject => subject.id === subjectId)
}

export const getSubjectColor = (subjectId: string): string => {
  const subject = getSubjectById(subjectId)
  return subject?.color || '#64748b'
}

export const getSubjectIcon = (subjectId: string): string => {
  const subject = getSubjectById(subjectId)
  return subject?.icon || '📚'
}

export const calculateSpacedRepetition = (
  easeFactor: number,
  interval: number,
  quality: number
): { newInterval: number; newEaseFactor: number } => {
  // SM-2 algorithm implementation
  let newEaseFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))
  
  if (newEaseFactor < 1.3) {
    newEaseFactor = 1.3
  }
  
  let newInterval: number
  
  if (quality < 3) {
    newInterval = 1
  } else {
    if (interval === 0) {
      newInterval = 1
    } else if (interval === 1) {
      newInterval = 6
    } else {
      newInterval = Math.round(interval * newEaseFactor)
    }
  }
  
  return { newInterval, newEaseFactor }
}

// Chart Utilities
export const generateChartColors = (count: number): string[] => {
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
    '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
    '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
    '#ec4899', '#f43f5e'
  ]
  
  const result: string[] = []
  for (let i = 0; i < count; i++) {
    result.push(colors[i % colors.length])
  }
  
  return result
}

export const createProgressData = (
  current: number,
  target: number,
  previousValue?: number
): ProgressData => {
  const percentage = calculatePercentage(current, target)
  let trend: 'up' | 'down' | 'stable' = 'stable'
  let change = 0
  
  if (previousValue !== undefined) {
    change = current - previousValue
    if (change > 0) trend = 'up'
    else if (change < 0) trend = 'down'
  }
  
  return {
    current,
    target,
    percentage,
    trend,
    change,
    period: 'current'
  }
}

// Error Handling
export const handleError = (error: any): string => {
  if (typeof error === 'string') return error
  if (error?.message) return error.message
  if (error?.response?.data?.message) return error.response.data.message
  return 'Beklenmeyen bir hata oluştu'
}

export const logError = (error: any, context?: string): void => {
  console.error(`Error${context ? ` in ${context}` : ''}:`, error)
  
  // In production, you might want to send this to an error tracking service
  if (import.meta.env.PROD) {
    // Send to error tracking service (e.g., Sentry, LogRocket, etc.)
  }
}

// Async Utilities
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export const retry = async <T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (retries > 0) {
      await sleep(delay)
      return retry(fn, retries - 1, delay * 2)
    }
    throw error
  }
}

export const timeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), ms)
    )
  ])
}

// Random Utilities
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9)
}

export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export const generateRandomColor = (): string => {
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
    '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
    '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef'
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

export const generateAvatar = (name: string): string => {
  const initials = name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
  
  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6']
  const color = colors[name.length % colors.length]
  
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" fill="${color}" rx="20"/>
      <text x="20" y="26" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="14" font-weight="bold">
        ${initials}
      </text>
    </svg>
  `)}`
}

// Export all utilities
export default {
  // Date & Time
  formatDate,
  formatRelativeTime,
  formatDuration,
  formatTime,
  parseTimeString,
  getTimeDifference,
  isToday,
  isThisWeek,
  isThisMonth,
  
  // String
  capitalize,
  capitalizeWords,
  slugify,
  truncate,
  removeHtmlTags,
  escapeHtml,
  unescapeHtml,
  
  // Number
  formatNumber,
  formatCurrency,
  formatPercentage,
  formatFileSize,
  clamp,
  roundToNearest,
  randomBetween,
  calculatePercentage,
  calculateGrade,
  
  // Array
  shuffle,
  chunk,
  unique,
  groupBy,
  sortBy,
  findByProperty,
  removeByProperty,
  
  // Object
  deepClone,
  mergeDeep,
  isObject,
  isEmpty,
  pick,
  omit,
  
  // Validation
  isValidEmail,
  isValidPassword,
  isValidPhone,
  isValidUsername,
  isValidName,
  isValidUrl,
  validateFileSize,
  validateFileType,
  
  // Color
  hexToRgb,
  rgbToHex,
  getContrastColor,
  lightenColor,
  darkenColor,
  
  // Storage
  setLocalStorage,
  getLocalStorage,
  removeLocalStorage,
  clearLocalStorage,
  setSessionStorage,
  getSessionStorage,
  removeSessionStorage,
  
  // URL
  getQueryParams,
  setQueryParam,
  removeQueryParam,
  buildUrl,
  
  // Device
  isMobile,
  isTablet,
  isDesktop,
  getDeviceType,
  getBrowserInfo,
  
  // Performance
  debounce,
  throttle,
  memoize,
  
  // Study
  calculateStudyStreak,
  calculateExamScore,
  getSubjectById,
  getSubjectColor,
  getSubjectIcon,
  calculateSpacedRepetition,
  
  // Chart
  generateChartColors,
  createProgressData,
  
  // Error
  handleError,
  logError,
  
  // Async
  sleep,
  retry,
  timeout,
  
  // Random
  generateId,
  generateUUID,
  generateRandomColor,
  generateAvatar
}